import { PresentationPageKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import {
  EducationListItem,
  EducationListItemType,
  getDefaultEducationTitle,
  PAGE_KIND_TO_EDUCATION_SLIDE_ID,
} from '../../Education.constants';
import { CUSTOM_SLIDE_PREFIX, DRAFT_SLIDE_ID } from '../../Education.sentinels';

// Re-export sentinel values for convenience
export { CUSTOM_SLIDE_PREFIX, DRAFT_SLIDE_ID } from '../../Education.sentinels';

/**
 * Maps EducationListItemType to the corresponding PresentationPageKind.
 * This is used to generate the slideIdentifier for the backend API.
 *
 * Note: This is the reverse of PAGE_KIND_TO_EDUCATION_SLIDE_ID.
 */
export const EDUCATION_SLIDE_ID_TO_PAGE_KIND: Partial<
  Record<EducationListItemType, PresentationPageKind>
> = Object.entries(PAGE_KIND_TO_EDUCATION_SLIDE_ID).reduce(
  (acc, [pageKind, educationSlideId]) => {
    acc[educationSlideId] = pageKind as PresentationPageKind;
    return acc;
  },
  {} as Partial<Record<EducationListItemType, PresentationPageKind>>
);

/**
 * Generates a unique slide identifier from a PresentationPageKind.
 * Format: {PresentationPageKind}_{pageIndex}
 *
 * This is the canonical format for slide identifiers used in the backend.
 * Use this when you already have a PresentationPageKind (e.g., from presentation pages).
 *
 * @param pageKind - The PresentationPageKind value
 * @param pageIndex - The index of the page within the slide (0-based)
 * @returns The slide identifier for the backend
 */
export function getSlideIdentifierFromPageKind(
  pageKind: PresentationPageKind,
  pageIndex: number
): string {
  return `${pageKind}_${pageIndex}`;
}

/**
 * Generates a unique slide identifier from an EducationListItemType.
 * Format: {PresentationPageKind}_{pageIndex}
 *
 * Use this when you have an EducationListItemType (e.g., from the education trowser/editor).
 * The function converts the education slide ID to its corresponding PresentationPageKind.
 *
 * Examples:
 * - CLT slide page 0 -> "CLT_STRATEGY_EXPLAINER_0"
 * - Why Estate Planning Matters page 0 -> "WHY_ESTATE_PLANNING_MATTERS_0"
 *
 * @param educationSlideId - The frontend education slide identifier
 * @param pageIndex - The index of the page within the slide (0-based)
 * @returns The slide identifier for the backend
 */
export function getSlideIdentifierFromEducationSlideId(
  educationSlideId: EducationListItemType,
  pageIndex: number
): string {
  const pageKind = EDUCATION_SLIDE_ID_TO_PAGE_KIND[educationSlideId];

  if (educationSlideId === DRAFT_SLIDE_ID) {
    return DRAFT_SLIDE_ID;
  }

  if (!pageKind) {
    // Fallback: use the education slide ID directly if no mapping exists
    // This should only happen if PAGE_KIND_TO_EDUCATION_SLIDE_ID is incomplete
    diagnostics.warn(
      `No PresentationPageKind mapping found for ${educationSlideId}, using direct value`
    );
    return `${educationSlideId}_${pageIndex}`;
  }

  return getSlideIdentifierFromPageKind(pageKind, pageIndex);
}

/**
 * Gets a human-readable display name for an education slide.
 *
 * @param educationSlideId - The frontend education slide identifier
 * @param pageIndex - The index of the page within the slide (0-based)
 * @param educationMap - Optional education map with overridden titles. If not provided, uses default titles.
 * @returns A display name for the slide config
 */
export function getSlideDisplayName(
  educationSlideId: EducationListItemType,
  pageIndex: number,
  educationMap?: Record<EducationListItemType, EducationListItem>
): string {
  // Use the provided map if available, otherwise fall back to default title
  const educationSlideTitle = educationMap
    ? educationMap[educationSlideId]?.title
    : getDefaultEducationTitle(educationSlideId);

  const baseName = educationSlideTitle ?? educationSlideId;

  // Add page number if there are multiple pages
  if (pageIndex > 0) {
    return `${baseName} (Page ${pageIndex + 1})`;
  }

  return baseName;
}

/**
 * Checks if a string is a custom slide identifier.
 * Custom slide identifiers start with CUSTOM_SLIDE_PREFIX.
 */
export function isCustomSlideIdentifier(identifier: string): boolean {
  return identifier.startsWith(CUSTOM_SLIDE_PREFIX);
}

/**
 * Generates a unique slide identifier for a new custom page.
 * Format: {CUSTOM_SLIDE_PREFIX}{timestamp}_{random}
 */
export function generateCustomSlideIdentifier(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `${CUSTOM_SLIDE_PREFIX}${timestamp}_${random}`;
}
